/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useCallback } from 'react';
import {
  Box,
  Checkbox,
  Text,
  Divider,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  Switch,
  Stack,
  Button,
  Input,
  HStack,
} from '@chakra-ui/react';
import debounce from 'lodash.debounce';
import { useAppSelector } from '../../../utils/reduxHooks';
import { selectSuppliersList } from '../../../modules/cart/selectors';
import {
  MIN_PRICE,
  MAX_PRICE,
  PRICE_SLIDER_STEP,
  MIN_DELIVERY_DAYS,
  MAX_DELIVERY_DAYS,
  DELIVERY_SLIDER_STEP,
  DEFAULT_DELIVERY_DAYS,
  DEFAULT_PRICE_RANGE,
  DEBOUNCE_DELAY_MS,
} from '../../../constants/filters';
import { useNavigate } from 'react-router-dom';

interface FiltersProps {
  onFilterChange: (filters: any) => void;
  isSearch?: boolean;
}

export const Filters: React.FC<FiltersProps> = ({ onFilterChange, isSearch }) => {
  const navigate = useNavigate();

  // Получаем список поставщиков из глобального состояния
  // const suppliersList = useAppSelector(selectSuppliersList);

  // Состояния для каждого фильтра
  const [priceRange, setPriceRange] = useState(DEFAULT_PRICE_RANGE);
  const [deliveryDays, setDeliveryDays] = useState(DEFAULT_DELIVERY_DAYS);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [originalOnly, setOriginalOnly] = useState(false);
  // const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);

  const handleBack = () => {
    navigate(-1);
  };

  // Мемоизированная функция для вызова onFilterChange с дебаунсом
  const debouncedFilterChange = useCallback(
    debounce(filters => {
      onFilterChange(filters);
    }, DEBOUNCE_DELAY_MS),
    [onFilterChange],
  );

  // Вызываем debouncedFilterChange при изменении фильтров
  const handlePriceRangeChange = (value: number[]) => {
    setPriceRange(value);
    debouncedFilterChange({
      priceRange: value,
      deliveryDays,
      inStockOnly,
      originalOnly,
      // suppliers: selectedSuppliers,
    });
  };

  const handleDeliveryDaysChange = (value: number[]) => {
    setDeliveryDays(value);
    debouncedFilterChange({
      priceRange,
      deliveryDays: value,
      inStockOnly,
      originalOnly,
      // suppliers: selectedSuppliers,
    });
  };

  const handleManualPriceChange = (index: number, value: string) => {
    const sanitizedValue = value.replace(/^0+(?=\d)/, '');
    const newPrice = [...priceRange];
    newPrice[index] = Math.min(Math.max(Number(sanitizedValue) || MIN_PRICE, MIN_PRICE), MAX_PRICE);
    setPriceRange(newPrice);
    debouncedFilterChange({
      priceRange: newPrice,
      deliveryDays,
      inStockOnly,
      originalOnly,
      // suppliers: selectedSuppliers,
    });
  };

  const handleManualDeliveryChange = (index: number, value: string) => {
    const sanitizedValue = value.replace(/^0+(?=\d)/, '');
    const newDelivery = [...deliveryDays];
    newDelivery[index] = Math.min(
      Math.max(Number(sanitizedValue) || MIN_DELIVERY_DAYS, MIN_DELIVERY_DAYS),
      MAX_DELIVERY_DAYS,
    );
    setDeliveryDays(newDelivery);
    debouncedFilterChange({
      priceRange,
      deliveryDays: newDelivery,
      inStockOnly,
      originalOnly,
      // suppliers: selectedSuppliers,
    });
  };

  const toggleInStockOnly = () => {
    const newInStockOnly = !inStockOnly;
    setInStockOnly(newInStockOnly);
    debouncedFilterChange({
      priceRange,
      deliveryDays,
      inStockOnly: newInStockOnly,
      originalOnly,
      // suppliers: selectedSuppliers,
    });
  };

  const toggleOriginalOnly = () => {
    const newOriginalOnly = !originalOnly;
    setOriginalOnly(newOriginalOnly);
    debouncedFilterChange({
      priceRange,
      deliveryDays,
      inStockOnly,
      originalOnly: newOriginalOnly,
      // suppliers: selectedSuppliers,
    });
  };

  // const handleSupplierChange = (supplierPk: string) => {
  //   const newSelectedSuppliers = selectedSuppliers.includes(supplierPk)
  //     ? selectedSuppliers.filter(s => s !== supplierPk)
  //     : [...selectedSuppliers, supplierPk];

  //   setSelectedSuppliers(newSelectedSuppliers);
  //   debouncedFilterChange({
  //     priceRange,
  //     deliveryDays,
  //     inStockOnly,
  //     originalOnly,
  //     suppliers: newSelectedSuppliers,
  //   });
  // };

  return (
    <Box pl={4} pr={8} width="300px" bg="white">
      {!isSearch ? (
        <Button onClick={handleBack} mb={4} variant="outline" colorScheme="blackAlpha">
          Назад
        </Button>
      ) : null}

      <Text fontWeight="bold" mb={2}>
        Цена
      </Text>
      <HStack spacing={4} mb={2}>
        <Input
          type="number"
          value={priceRange[0] + ''.replace(/^0+(?=\d)/, '')}
          onChange={e => handleManualPriceChange(0, e.target.value)}
        />
        <Text>—</Text>
        <Input
          type="number"
          value={priceRange[1] + ''.replace(/^0+(?=\d)/, '')}
          onChange={e => handleManualPriceChange(1, e.target.value)}
        />
      </HStack>
      <RangeSlider
        min={MIN_PRICE}
        max={MAX_PRICE}
        value={priceRange}
        onChange={setPriceRange} 
        onChangeEnd={handlePriceRangeChange}
        step={PRICE_SLIDER_STEP}
        colorScheme="blackAlpha"
      >
        <RangeSliderTrack>
          <RangeSliderFilledTrack bg="black" />
        </RangeSliderTrack>
        <RangeSliderThumb index={0} bg="black" />
        <RangeSliderThumb index={1} bg="black" />
      </RangeSlider>

      <Divider my={4} />

      <Text fontWeight="bold" mb={2}>
        Срок поставки, дней
      </Text>
      <HStack spacing={4} mb={2}>
        <Input
          type="number"
          value={deliveryDays[0] + ''.replace(/^0+(?=\d)/, '')}
          onChange={e => handleManualDeliveryChange(0, e.target.value)}
        />
        <Text>—</Text>
        <Input
          type="number"
          value={deliveryDays[1] + ''.replace(/^0+(?=\d)/, '')}
          onChange={e => handleManualDeliveryChange(1, e.target.value)}
        />
      </HStack>
      <RangeSlider
        min={MIN_DELIVERY_DAYS}
        max={MAX_DELIVERY_DAYS}
        value={deliveryDays}
        onChange={setDeliveryDays} 
        onChangeEnd={handleDeliveryDaysChange}
        step={DELIVERY_SLIDER_STEP}
        colorScheme="blackAlpha"
      >
        <RangeSliderTrack>
          <RangeSliderFilledTrack bg="black" />
        </RangeSliderTrack>
        <RangeSliderThumb index={0} bg="black" />
        <RangeSliderThumb index={1} bg="black" />
      </RangeSlider>

      <Divider my={4} />

      <Stack direction="row" mb={4} align="center">
        <Text>Только в наличии</Text>
        <Switch isChecked={inStockOnly} onChange={toggleInStockOnly} />
      </Stack>

      <Stack direction="row" mb={4} align="center">
        <Text>Только оригинал</Text>
        <Switch isChecked={originalOnly} onChange={toggleOriginalOnly} />
      </Stack>

      <Divider my={4} />

      {/* <Text fontWeight="bold" mb={2}>
        Поставщики
      </Text>
      <Stack direction="column">
        {suppliersList.map(supplier => (
          <Checkbox
            key={supplier.pk}
            isChecked={selectedSuppliers.includes(supplier.pk)}
            onChange={() => handleSupplierChange(supplier.pk)}
          >
            {supplier.name}
          </Checkbox>
        ))}
      </Stack> */}
    </Box>
  );
};
